import { Combobox, InputTextCustom, CustomTable, GetItemsProps, CustomTableRef, formatCurrency, Notification, NotificationRef, ETypeOfNotification, Steps, setNotification } from "medere-ui-framework"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { usePaymentOrder } from "../../components/context/PaymentOrderContext"
import PaymentOrderHeader from "./PaymentOrderHeader"
import { useEffect, useRef, useState } from "react"
import { CashPaymentMethod, CheckPaymentMethod, DebitPaymentMethod } from "./PaymentOrderMethodItems"
import Big from "big.js"
import { MAX_DECIMAL } from "../../interface/orderOfProvision"
import { EAdministrativeFormType } from "../../enums/EAdministrativeFormType"
import { useDispatch } from "react-redux"
import { useBankService } from "../../servicesHooks/Bank/useBankService"
import { usePaymentOrdersService } from "../../servicesHooks/PaymentOrders/usePaymentOrdersService"
import { useSupplierService } from "../../servicesHooks/Supplier/useSupplierService"
import { FinancialAccountDTO } from "../../dto/FinancialAccountDTO"
import FinancialAccountCombo from "../../components/FinancialAccountCombo"



export interface PaymentOrderMethod {
    bankAccount: any
    paymentMethod: any
    paymentDate: any
    formNumber: number|null
    checkNumber: number|null
    observations: string|null
    amount: Big
    financialAccount: FinancialAccountDTO | null
}

enum EPaymentMethod {
    BANKACCOUNTDEBIT = 2,
    CREDITCARD = 3,
    BANKTRANSFER = 5,
    EFECTIVO = 9,
    CHEQUE = 10,
    IMPUESTO = 11,
    MERCADO_PAGO = 13,
    CREDIT_ACCOUNT = 15
}

const initialPaymentOrderMethod: PaymentOrderMethod = {
    bankAccount: null,
    paymentMethod: null,
    paymentDate: null,
    formNumber: null,
    checkNumber: null,
    observations: null,
    amount: Big(0),
    financialAccount: null
}


function PaymentMethod() {
    const dispatch = useDispatch();
    const { getAllBankAccountsComboByMedereEntity, getPaymentMethodsByBankAccount } = useBankService();
    const { exportPaymentOrderPDF, savePaymentOrder } = usePaymentOrdersService();
    const { generateAutomaticAccountingMovement } = useSupplierService();
    const { stepMovement, steps, invoices, supplier, paymentArray, totalWithoutTaxes, totalToPay, taxes, paidTaxes, cleanAll } = usePaymentOrder()
    const [paymentOrderMethod, setPaymentOrderMethod] = useState<PaymentOrderMethod>(initialPaymentOrderMethod)
    const [paymentOrderMethods, setPaymentOrderMethods] = useState<PaymentOrderMethod[]>([])
    const tableRef = useRef<CustomTableRef>(null)

    function validatePaymentOrderMethod() {
        if (!paymentOrderMethod.bankAccount) {
            return false
        }
        if (!paymentOrderMethod.paymentMethod) {
            return false
        }
        if (!paymentOrderMethod.paymentDate) {
            return false
        }
        if (!paymentOrderMethod.amount.gt(0)) {
            return false
        }
        if (!paymentOrderMethod.financialAccount) {
            return false
        }
        if (!paymentOrderMethod.checkNumber && paymentOrderMethod.paymentMethod.paymentmethod === EPaymentMethod.CHEQUE) {
            return false
        }
        if (!paymentOrderMethod.formNumber && paymentOrderMethod.paymentMethod.paymentmethod === EPaymentMethod.BANKTRANSFER) {
            return false
        }
        return true
    }

    function addToPaymentOrderMethods() {
        if (validatePaymentOrderMethod()) {
            setPaymentOrderMethods((prev) => [...prev, paymentOrderMethod])
            setPaymentOrderMethod(initialPaymentOrderMethod)
            return
        }

        dispatch(setNotification({
            title: 'Orden de pago',
            desc: 'Por favor complete los campos requeridos',
            typeOfNotification: ETypeOfNotification.ERROR
        }))
    }

    async function generatePaymentOrder() {
        let accumulated = Big(0);

        if (Array.isArray(paymentOrderMethods)) {
            for (const method of paymentOrderMethods) {
                accumulated = accumulated.plus(Big(method?.amount ? method.amount : 0));
            }
        }

        if (!paymentOrderMethods?.reduce((acc, curr) => acc.plus(curr.amount), Big(0)).eq(totalToPay)) {
            dispatch(setNotification({
                title: 'Orden de pago',
                desc: 'El monto total de la orden de pago no coincide con el monto total a pagar',
                typeOfNotification: ETypeOfNotification.ERROR
            }))
            return
        }

        const _paymentOrder = await savePaymentOrder({
            totalamountbanks: Number(calculateTotalByMethod(EPaymentMethod.BANKTRANSFER)),
            totalamountothermethods: Number(calculateTotalByMethod(EPaymentMethod.EFECTIVO)),
            totalamountchecks: Number(calculateTotalByMethod(EPaymentMethod.CHEQUE)),
            totalamounttopay: Number(totalToPay),
            totalamountwithoutretentions: Number(totalWithoutTaxes),
            retentionamountiibb: Number(taxes.iibb),
            retentionamountearnings: Number(taxes.earnings),
            retentionamountothers: Number(taxes.others),
            invoicestopay: paymentArray.map((item) => {
                const administrativeForm = invoices.find((invoice:any) => `${invoice.posnumber}|${invoice.formnumber}` == item.orderId && item.paymentValue == invoice.amount).administrativeform
                return {
                    administrativeform: administrativeForm,
                    amounttopay: item.paymentToPay.toFixed(MAX_DECIMAL)
                }
            }),
            paymentorders: paymentOrderMethods.map((item) => {
                return {
                    bankaccount: item.bankAccount.bankaccount,
                    amount: item.amount,
                    checknumber: item.checkNumber,
                    formnumber: item.formNumber,
                    observations: item.observations,
                    paymentdate: item.paymentDate,
                    paymentmethod: item.paymentMethod.paymentmethod,
                    financialAccountId: item.financialAccount!.financialAccount
                }
            }),
            paidtaxes: paidTaxes.map((paidtax) => {
                return {
                    taxtype: paidtax.taxtype,
                    amount: paidtax.amount,
                    percentage: paidtax.percentage
                }
            })
        })
            .then(async (resp) => {
                setPaymentOrderMethods([])
                cleanAll()
                await generateAutomaticAccountingMovement({ administrativeFormId: resp })
                dispatch(setNotification({
                    title: 'Orden de pago',
                    desc: 'Orden de pago generada con éxito',
                    typeOfNotification: ETypeOfNotification.INFO,
                    icon: 'pi pi-check'
                }))
                return resp
            })
            .catch((error: any) => {
                const backendMessage = error?.response?.data?.message;
                dispatch(setNotification({
                    title: 'Orden de pago',
                    desc: backendMessage || 'Error al generar orden de pago.',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }))
            })
        if (_paymentOrder) {
            exportPaymentOrderPDF(_paymentOrder).then(() => {
                dispatch(setNotification({
                    title: 'Orden de pago',
                    desc: 'Orden de pago generada con éxito (PDF)',
                    typeOfNotification: ETypeOfNotification.INFO,
                    icon: 'pi pi-check'
                }))

            }).catch(() => {
                dispatch(setNotification({
                    title: 'Orden de pago',
                    desc: 'Error al generar la orden de pago(PDF)',
                    typeOfNotification: ETypeOfNotification.ERROR
                }))
            })
        }
    }


    useEffect(() => {
        if (tableRef.current?.refreshTable) {
            tableRef.current.refreshTable()
        }
    }, [paymentOrderMethods])

    useEffect(() => {
        const totalPaid = paymentOrderMethods.reduce((acc, method) => acc.plus(method.amount), Big(0));
        const remainingToPay = totalToPay.minus(totalPaid);

        setPaymentOrderMethod((prev) => ({
            ...prev,
            amount: remainingToPay.gt(0) ? remainingToPay : Big(0),
        }));
    }, [paymentOrderMethods, totalToPay]);


    function switchPaymentMethod() {
        switch (paymentOrderMethod.paymentMethod?.paymentmethod) {
            case EPaymentMethod.BANKTRANSFER:
                return <DebitPaymentMethod
                    paymentOrderMethod={paymentOrderMethod}
                    setPaymentOrderMethod={setPaymentOrderMethod}
                    onClick={addToPaymentOrderMethods}
                >
                    <Combobox
                        getItems={getAllBankAccountsComboByMedereEntity}
                        optionLabel="bankdesc"
                        params={{
                            medereentity: supplier?.company,
                            onlyWithPaymentMethods: true
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, bankAccount: e }))
                        }
                        label="Cuenta bancaria"
                        value={paymentOrderMethod.bankAccount}
                    />
                    <Combobox
                        getItems={getPaymentMethodsByBankAccount}
                        optionLabel="methodname"
                        params={{
                            bankAccountId: paymentOrderMethod.bankAccount?.bankaccount,
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, paymentMethod: e }))
                        }
                        label="Tipo"
                        value={paymentOrderMethod.paymentMethod}
                    />
                    <FinancialAccountCombo
                        value={paymentOrderMethod.financialAccount}
                        onChange={(e) => setPaymentOrderMethod((prev) => ({ ...prev, financialAccount: e }))}
                        required
                    />
                </DebitPaymentMethod>
            case EPaymentMethod.CHEQUE:
                return <CheckPaymentMethod
                    paymentOrderMethod={paymentOrderMethod}
                    setPaymentOrderMethod={setPaymentOrderMethod}
                    onClick={addToPaymentOrderMethods}
                >
                    <Combobox
                        getItems={getAllBankAccountsComboByMedereEntity}
                        optionLabel="bankdesc"
                        params={{
                            medereentity: supplier?.company,
                            onlyWithPaymentMethods: true
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, bankAccount: e }))
                        }
                        label="Cuenta bancaria"
                        value={paymentOrderMethod.bankAccount}
                    />
                    <Combobox
                        getItems={getPaymentMethodsByBankAccount}
                        optionLabel="methodname"
                        params={{
                            bankAccountId: paymentOrderMethod.bankAccount?.bankaccount,
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, paymentMethod: e }))
                        }
                        label="Tipo"
                        value={paymentOrderMethod.paymentMethod}
                    />
                    <FinancialAccountCombo
                        value={paymentOrderMethod.financialAccount}
                        onChange={(e) => setPaymentOrderMethod((prev) => ({ ...prev, financialAccount: e }))}
                        required
                    />
                </CheckPaymentMethod>
            case EPaymentMethod.EFECTIVO:
                return <CashPaymentMethod
                    paymentOrderMethod={paymentOrderMethod}
                    setPaymentOrderMethod={setPaymentOrderMethod}
                    onClick={addToPaymentOrderMethods}
                >
                    <Combobox
                        getItems={getAllBankAccountsComboByMedereEntity}
                        optionLabel="bankdesc"
                        params={{
                            medereentity: supplier?.company,
                            onlyWithPaymentMethods: true
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, bankAccount: e }))
                        }
                        label="Cuenta bancaria"
                        value={paymentOrderMethod.bankAccount}
                    />
                    <Combobox
                        getItems={getPaymentMethodsByBankAccount}
                        optionLabel="methodname"
                        params={{
                            bankAccountId: paymentOrderMethod.bankAccount?.bankaccount,
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, paymentMethod: e }))
                        }
                        label="Tipo"
                        value={paymentOrderMethod.paymentMethod}
                    />
                    <FinancialAccountCombo
                        value={paymentOrderMethod.financialAccount}
                        onChange={(e) => setPaymentOrderMethod((prev) => ({ ...prev, financialAccount: e }))}
                        required
                    />
                </CashPaymentMethod>
            default:
                return <CashPaymentMethod
                    paymentOrderMethod={paymentOrderMethod}
                    setPaymentOrderMethod={setPaymentOrderMethod}
                    onClick={addToPaymentOrderMethods}
                >
                    <Combobox
                        getItems={getAllBankAccountsComboByMedereEntity}
                        optionLabel="bankdesc"
                        params={{
                            medereentity: supplier?.company,
                            onlyWithPaymentMethods: true
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, bankAccount: e }))
                        }
                        label="Cuenta bancaria"
                        value={paymentOrderMethod.bankAccount}
                    />
                    <Combobox
                        getItems={getPaymentMethodsByBankAccount}
                        optionLabel="methodname"
                        params={{
                            bankAccountId: paymentOrderMethod.bankAccount?.bankaccount,
                        }}
                        setValue={(e) =>
                            setPaymentOrderMethod((prev) => ({ ...prev, paymentMethod: e }))
                        }
                        label="Tipo"
                        value={paymentOrderMethod.paymentMethod}
                        disable={!paymentOrderMethod.bankAccount}
                    />
                    <FinancialAccountCombo
                        value={paymentOrderMethod.financialAccount}
                        onChange={(e) => setPaymentOrderMethod((prev) => ({ ...prev, financialAccount: e }))}
                        required
                    />
                </CashPaymentMethod>

        }
    }

    function calculateTotalByMethod(method: EPaymentMethod) {
        return paymentOrderMethods
            ?.filter(x => x.paymentMethod?.paymentmethod === method)
            .reduce((acc, curr) => acc.plus(curr.amount), Big(0))
            .toFixed(MAX_DECIMAL)
    }

    return (
        <>
            <Steps steps={steps} activeStep={stepMovement.stepIndex} labelStyle={{ color: '#01717C', fontSize: '16px', fontWeight: '500' }} />
            <PaymentOrderHeader fullInformation />
            <div className="emr-divider-soft my-2"></div>

            {switchPaymentMethod()}
            <div>
                <CustomTable
                    idTable="paymentMethodTable"
                    getItems={() => Promise.resolve(paymentOrderMethods)}
                    hideFiltersButton
                    resizableColumns
                    className="my-2"
                    ref={tableRef}
                    deleteTableItem
                    onDeleteHandler={(e) => {
                        setPaymentOrderMethods((prev) => prev.filter((item) => item !== e))
                    }}
                >
                    <Column header='Fecha de pago' body={(e) => {
                        return new Date(e.paymentDate).toLocaleDateString()
                    }} />
                    <Column header='Tipo' body={(e) => {
                        return e.paymentMethod?.methodname
                    }} />
                    <Column header='Cuenta bancaria' body={(e) => {
                        return e.bankAccount?.bankdesc
                    }} />
                    <Column header='Cuenta financiera' body={(e) => {
                        return e.financialAccount?.name
                    }} />
                    <Column header='Nº de cheque' field="checkNumber" />
                    <Column header='Nº de comprobante' field="formNumber" />
                    <Column header='Observación' field="observations" />
                    <Column header='Importe' field="amount" body={(e) => {
                        return formatCurrency(Number(e?.amount ? e.amount : 0), {
                            currency: 'ARS',
                            maximumFractionDigits: MAX_DECIMAL
                        })
                    }} />
                </CustomTable>
            </div>
            <section className="payment-order-method-grid">
                <p>Total bancos propios</p>
                <p>$ {calculateTotalByMethod(EPaymentMethod.BANKTRANSFER)}</p>
                <p>Total otros medios</p>
                <p>$ {calculateTotalByMethod(EPaymentMethod.EFECTIVO)}</p>
                <p>Total cheques a terceros</p>
                <p>$ {calculateTotalByMethod(EPaymentMethod.CHEQUE)}</p>
                <p className="font-bold uppercase title">Total a pagar</p>
                <p className="font-bold uppercase title">$ {paymentOrderMethods?.reduce((acc, curr) => acc.plus(curr.amount), Big(0)).toFixed(MAX_DECIMAL)}</p>
            </section>
            <div className="flex flex-end align-items-center">
                <Button className="buttonMain2 capitalize" label="volver" iconPos="left" icon="pi pi-chevron-left" onClick={stepMovement.prevStep} />
                <Button className="buttonMain" label="Generar orden de pago" iconPos="right" icon="pi pi-chevron-right" onClick={generatePaymentOrder} />
            </div>
        </>
    )
}

export default PaymentMethod
